/**
 * Shared product-mock visuals for the landing page.
 * Pure presentational — no data, no state. Built only from existing design tokens.
 */

// ─── Trend bars (fake 14-day chart) ────────────────────────────────────────────
const TREND_DATA = [14, 22, 18, 35, 28, 44, 38, 52, 42, 58, 66, 74, 62, 88];
export function TrendBars({ animate = true, className = "" }) {
  const max = Math.max(...TREND_DATA);
  return (
    <div className={`flex h-14 w-full items-end gap-[3px] ${className}`}>
      {TREND_DATA.map((v, i) => (
        <div
          key={i}
          className="flex-1 rounded-sm border border-ink/30 transition-all duration-500"
          style={{
            height: `${(v / max) * 100}%`,
            background: i >= 10 ? "#8B5CF6" : "#EDE9FE",
            transitionDelay: animate ? `${i * 40}ms` : "0ms",
          }}
        />
      ))}
    </div>
  );
}

// ─── Mini heatmap ───────────────────────────────────────────────────────────────
const HEAT_DATA = [
  [0, 0, 1, 2, 1, 0, 0],
  [0, 1, 3, 4, 3, 1, 0],
  [1, 2, 5, 8, 6, 2, 1],
  [0, 1, 4, 7, 5, 2, 0],
  [0, 0, 2, 3, 2, 1, 0],
];
export function MiniHeatmap() {
  const days = ["M", "T", "W", "T", "F", "S", "S"];
  const getColor = (v) => {
    if (v === 0) return "bg-line/50";
    if (v <= 2) return "bg-candy/20";
    if (v <= 4) return "bg-candy/45";
    if (v <= 6) return "bg-candy/70";
    return "bg-candy";
  };
  return (
    <div className="flex flex-col gap-1">
      <div className="mb-0.5 flex gap-1">
        {days.map((d, i) => (
          <div
            key={i}
            className="flex-1 text-center font-mono text-[8px] uppercase tracking-widest text-muted-foreground"
          >
            {d}
          </div>
        ))}
      </div>
      {HEAT_DATA.map((row, ri) => (
        <div key={ri} className="flex gap-1">
          {row.map((v, ci) => (
            <div
              key={ci}
              className={`aspect-square flex-1 rounded-[3px] border border-ink/10 ${getColor(v)}`}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── QR glyph ─────────────────────────────────────────────────────────────────
export function QRGlyph({ cells = 5, size = "h-4 w-4" }) {
  const filled = [0, 1, 2, 3, 4, 5, 9, 10, 14, 15, 19, 20, 21, 22, 23, 24, 6, 12, 18];
  return (
    <div
      className={`grid gap-0.5`}
      style={{ gridTemplateColumns: `repeat(${cells}, minmax(0,1fr))` }}
    >
      {Array.from({ length: cells * cells }).map((_, i) => (
        <div
          key={i}
          className={`${size} rounded-[2px] ${filled.includes(i % 25) ? "bg-ink" : "bg-transparent"}`}
        />
      ))}
    </div>
  );
}

// ─── Dashboard mock (browser-framed) ────────────────────────────────────────────
export function DashboardMock({ animate = true }) {
  return (
    <div className="overflow-hidden rounded-2xl border-2 border-ink bg-paper shadow-sticker-lg">
      {/* Browser chrome */}
      <div className="flex items-center gap-3 border-b-2 border-ink bg-ink px-4 py-3">
        <div className="flex shrink-0 gap-1.5">
          <div className="h-3 w-3 rounded-full border border-ink/30 bg-pink" />
          <div className="h-3 w-3 rounded-full border border-ink/30 bg-yellow" />
          <div className="h-3 w-3 rounded-full border border-ink/30 bg-mint" />
        </div>
        <div className="flex-1 truncate rounded-full bg-white/10 px-4 py-1.5 text-center font-mono text-[10px] tracking-widest text-white/60">
          linklane.io/dashboard
        </div>
        <div className="w-10 shrink-0" />
      </div>

      {/* Body */}
      <div className="p-5 md:p-6">
        <div className="mb-5 flex items-end justify-between gap-4">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full border-2 border-ink bg-yellow-tint px-3 py-1 font-mono text-[9px] uppercase tracking-widest shadow-sticker-xs">
              LINKLANE — DASHBOARD
            </div>
            <div className="font-display text-2xl font-extrabold leading-tight md:text-3xl">
              Mission Control.
            </div>
          </div>
          <div className="hidden gap-2 md:flex">
            <div className="rounded-full border-2 border-ink bg-white px-4 py-1.5 font-mono text-[10px] uppercase tracking-widest shadow-sticker-xs">
              + Open lane
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-5 grid grid-cols-3 gap-3">
          {[
            { label: "EDITIONS", value: "24", tone: "candy" },
            { label: "CLICKS", value: "4,891", tone: "pink" },
            { label: "14-DAY TREND", value: null, tone: "mint" },
          ].map(({ label, value, tone }) => {
            const tintBg = { candy: "bg-candy-tint", pink: "bg-pink-tint", mint: "bg-mint-tint" };
            const shadowTone = {
              candy: "shadow-sticker-candy",
              pink: "shadow-sticker-pink",
              mint: "shadow-sticker-mint",
            };
            return (
              <div
                key={label}
                className={`border-2 border-ink ${shadowTone[tone]} rounded-2xl p-4 ${tintBg[tone]}`}
              >
                <div className="mb-2 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                  {label}
                </div>
                {value ? (
                  <div className="font-display text-3xl font-extrabold">{value}</div>
                ) : (
                  <TrendBars animate={animate} />
                )}
              </div>
            );
          })}
        </div>

        {/* Link table */}
        <div className="overflow-hidden rounded-2xl border-2 border-ink shadow-sticker-xs">
          <div className="flex items-center gap-3 border-b-2 border-ink bg-candy-tint px-4 py-2">
            <div className="flex-1 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
              URL
            </div>
            <div className="w-16 text-right font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
              CLICKS
            </div>
            <div className="w-20" />
          </div>
          {[
            {
              code: "product-hunt",
              url: "producthunt.com/posts/linklane",
              clicks: "2,847",
              tone: "candy",
            },
            {
              code: "summer-drop",
              url: "shop.example.com/summer-collection",
              clicks: "891",
              tone: "pink",
            },
            { code: "docs", url: "docs.example.com/getting-started", clicks: "234", tone: "mint" },
          ].map(({ code, url, clicks, tone }, i) => {
            const chipCls = {
              candy: "bg-candy-tint text-candy-dark border-candy/40",
              pink: "bg-pink-tint text-ink border-pink/40",
              mint: "bg-mint-tint text-ink border-mint/40",
            };
            return (
              <div
                key={code}
                className={`flex items-center gap-3 px-4 py-3 ${i < 2 ? "border-b border-line" : ""}`}
              >
                <div
                  className={`shrink-0 rounded-full border px-2.5 py-0.5 font-mono text-[10px] font-bold ${chipCls[tone]}`}
                >
                  /{code}
                </div>
                <div className="hidden flex-1 truncate font-sans text-xs text-muted-foreground md:block">
                  {url}
                </div>
                <div className="w-16 text-right font-display text-sm font-bold">{clicks}</div>
                <div className="flex w-20 justify-end gap-1">
                  {["↗", "✎", "✕"].map((icon) => (
                    <span
                      key={icon}
                      className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-ink bg-white text-[10px] shadow-sticker-xs"
                    >
                      {icon}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Phone bio-page mock ────────────────────────────────────────────────────────
export function PhoneBioMock({ theme }) {
  // theme: { bg, accent, name, font } — defaults to the "Candy" pro theme look
  const bg = theme?.bg || "linear-gradient(160deg, #F3EEFF 0%, #FCE7F3 100%)";
  const accent = theme?.accent || "#8B5CF6";
  const font = theme?.font || "'Outfit', sans-serif";
  const links = ["Latest video ↗", "Shop my picks", "Newsletter", "Book a call"];
  return (
    <div className="relative w-[180px] shrink-0 rounded-[2rem] border-[3px] border-ink bg-ink p-2 shadow-sticker-md">
      {/* notch */}
      <div className="absolute left-1/2 top-2 z-10 h-3 w-16 -translate-x-1/2 rounded-full bg-ink" />
      <div
        className="flex h-[340px] flex-col items-center overflow-hidden rounded-[1.5rem] px-4 pb-4 pt-7"
        style={{ background: bg, fontFamily: font }}
      >
        <div
          className="h-12 w-12 rounded-full border-2 border-ink shadow-sticker-xs"
          style={{ background: accent }}
        />
        <div className="mt-2 font-display text-sm font-extrabold" style={{ color: "#1E293B" }}>
          @yourhandle
        </div>
        <div className="mt-0.5 text-center font-sans text-[10px] leading-snug text-ink/60">
          Creator · building in public
        </div>
        <div className="mt-3 flex w-full flex-col gap-2">
          {links.map((l) => (
            <div
              key={l}
              className="w-full rounded-xl border-2 border-ink bg-white/85 py-2 text-center font-display text-[11px] font-bold shadow-sticker-xs"
              style={{ color: "#1E293B" }}
            >
              {l}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Analytics card mock ─────────────────────────────────────────────────────────
export function AnalyticsMock({ animate = true }) {
  return (
    <div className="overflow-hidden rounded-3xl border-2 border-ink bg-paper shadow-sticker-lg">
      <div className="flex items-center justify-between border-b-2 border-ink bg-candy-tint px-5 py-3">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.28em] text-muted-foreground">
            LINK ANALYTICS
          </div>
          <div className="mt-0.5 font-display text-sm font-bold text-ink">
            linklane.io/<span className="text-candy">product-hunt</span>
          </div>
        </div>
        <div className="rounded-full border-2 border-ink bg-white px-3 py-1 font-mono text-[9px] text-ink shadow-sticker-xs">
          14 DAYS
        </div>
      </div>
      <div className="grid grid-cols-4 divide-x-2 divide-ink border-b-2 border-ink">
        {[
          { label: "Clicks", value: "2,847" },
          { label: "Unique", value: "1,923" },
          { label: "Countries", value: "34" },
          { label: "QR Scans", value: "312" },
        ].map((s) => (
          <div key={s.label} className="px-3 py-3">
            <div className="font-display text-xl font-extrabold">{s.value}</div>
            <div className="mt-1 font-mono text-[8px] uppercase tracking-widest text-muted-foreground">
              {s.label}
            </div>
          </div>
        ))}
      </div>
      <div className="border-b-2 border-ink p-4">
        <div className="mb-2 font-mono text-[9px] uppercase tracking-[0.24em] text-muted-foreground">
          CLICK TREND
        </div>
        <TrendBars animate={animate} />
      </div>
      <div className="grid grid-cols-3 divide-x-2 divide-ink">
        {[
          { label: "Mobile", pct: "62%" },
          { label: "Top: US", pct: "38%" },
          { label: "Heatmap", pct: null },
        ].map(({ label, pct }) => (
          <div key={label} className="px-4 py-3">
            <div className="mb-1.5 font-mono text-[8px] uppercase tracking-widest text-muted-foreground">
              {label}
            </div>
            {pct ? (
              <div className="font-display text-lg font-extrabold">{pct}</div>
            ) : (
              <MiniHeatmap />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
